import React, { useId } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  Home,
  LocalLibrary,
  QrCode2,
  MonetizationOn,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';

// Profile entry removed — the AppBar avatar handles profile navigation on every page.
const NAV_ITEMS = [
  { key: 'home', label: 'Home', icon: Home, path: '/student' },
  { key: 'libraries', label: 'Libraries', icon: LocalLibrary, path: '/student/libraries' },
  { key: 'access', label: 'Access', icon: QrCode2, path: '/student/access' },
  { key: 'coins', label: 'Coins', icon: MonetizationOn, path: '/student/coins' },
];

function getActiveKey(pathname) {
  if (pathname === '/student' || pathname === '/student/') return 'home';
  if (pathname.startsWith('/student/libraries')) return 'libraries';
  if (pathname.startsWith('/student/access') || pathname.startsWith('/student/qr-pass')) return 'access';
  if (pathname.startsWith('/student/coins')) return 'coins';
  return null;
}

/**
 * StudentBottomNav
 * Four-item mobile bottom navigation with a framer-motion layoutId pill indicator.
 * Hidden on desktop (≥md breakpoint).
 */
function StudentBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const layoutId = useId();
  const activeKey = getActiveKey(location.pathname);

  return (
    <Box
      component="nav"
      aria-label="Student navigation"
      sx={{
        bgcolor: 'background.paper',
        borderTop: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 -2px 8px rgba(0,0,0,0.4)'
          : '0 -2px 6px rgba(15,23,42,0.04)',
        display: { xs: 'flex', md: 'none' },
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {NAV_ITEMS.map((item) => {
        const isActive = item.key === activeKey;
        const IconComponent = item.icon;

        return (
          <Box
            key={item.key}
            role="tab"
            aria-selected={isActive}
            aria-current={isActive ? 'page' : undefined}
            tabIndex={0}
            onClick={() => navigate(item.path)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') navigate(item.path);
            }}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 1,
              pt: 1.25,
              cursor: 'pointer',
              position: 'relative',
              outline: 'none',
              minHeight: 64,
              '&:focus-visible': {
                boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
              },
            }}
          >
            {/* Sliding active pill behind icon */}
            {isActive && (
              <motion.div
                layoutId={layoutId}
                style={{
                  position: 'absolute',
                  top: 6,
                  width: 48,
                  height: 32,
                  borderRadius: 16,
                  background: theme.palette.primary.soft ?? 'rgba(242,101,34,0.12)',
                  zIndex: 0,
                }}
                transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              />
            )}

            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <IconComponent
                sx={{
                  fontSize: 22,
                  color: isActive ? 'primary.main' : 'text.secondary',
                  transition: 'color 0.15s',
                }}
              />
            </Box>

            <Typography
              variant="overline"
              sx={{
                fontSize: '0.6875rem',
                fontWeight: isActive ? 700 : 500,
                color: isActive ? 'primary.main' : 'text.secondary',
                lineHeight: 1,
                mt: 0.25,
                letterSpacing: 0,
                textTransform: 'none',
                zIndex: 1,
              }}
            >
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default StudentBottomNav;
